import { useState, useEffect } from 'react';
import { KnowledgeSet } from '@/types/knowledge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

interface KnowledgeSetEditDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (setData: Partial<KnowledgeSet>) => void;
    knowledgeSet: KnowledgeSet | null;
}

export const KnowledgeSetEditDialog = ({
    isOpen,
    onClose,
    onSave,
    knowledgeSet,
}: KnowledgeSetEditDialogProps) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        prompt_direction: '',
    });

    useEffect(() => {
        if (knowledgeSet) {
            setFormData({
                name: knowledgeSet.name || '',
                description: knowledgeSet.description || '',
                prompt_direction: knowledgeSet.prompt_direction || '',
            });
        } else {
            setFormData({
                name: '',
                description: '',
                prompt_direction: '',
            });
        }
    }, [knowledgeSet, isOpen]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSubmit = () => {
        if (!formData.name.trim()) {
            return;
        }
        onSave(formData);
        onClose();
    };

    const getInputStyles = () => ({
        borderColor: "hsl(var(--muted-foreground))",
    });

    const handleInputFocus = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        e.target.style.borderColor = "hsl(var(--primary))";
        e.target.style.boxShadow = "0 0 0 2px hsl(var(--primary) / 0.2)";
    };

    const handleInputBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        e.target.style.borderColor = "hsl(var(--muted-foreground))";
        e.target.style.boxShadow = 'none';
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="bg-background">
                <DialogHeader>
                    <DialogTitle className="text-foreground">
                        {knowledgeSet ? 'Edit Knowledge Set' : 'Create Knowledge Set'}
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        {knowledgeSet
                            ? 'Update the knowledge set details. Only name, description, and prompt direction can be changed.'
                            : 'Create a new knowledge set. Name, description, and prompt direction are editable.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    <div>
                        <label
                            className="block text-sm font-medium mb-1 text-foreground"
                        >
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg outline-none transition-colors"
                            style={getInputStyles()}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Enter knowledge set name"
                        />
                    </div>

                    <div>
                        <label
                            className="block text-sm font-medium mb-1 text-foreground"
                        >
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-3 py-2 border rounded-lg outline-none transition-colors"
                            style={getInputStyles()}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Describe the knowledge set"
                        />
                    </div>

                    <div>
                        <label
                            className="block text-sm font-medium mb-1 text-foreground"
                        >
                            Prompt Direction
                        </label>
                        <textarea
                            name="prompt_direction"
                            value={formData.prompt_direction}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-3 py-2 border rounded-lg outline-none transition-colors"
                            style={getInputStyles()}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            placeholder="Instructions on how to use this knowledge set in prompts"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border rounded-md border-border text-foreground hover:bg-muted transition-colors bg-background"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!formData.name.trim()}
                        className="px-4 py-2 rounded-md transition-colors bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        {knowledgeSet ? 'Update' : 'Create'}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
